import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@/Common/helpers/logger";
import { IWashoutPdfUrl } from "@/Washouts/types/Api/commons";
import { ISystemSettings } from "@/Settings/types/Common/model";
import { IGetAllReviewersSystemSettings, IGetChangeRequestsByCommissionCycle } from "@/Washouts/types/PayrollClosing/queries";
import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import { ChangeRequestResponseFragment, SidePanelTreeFragment } from "./fragments";

export async function getWashoutsSidePanelTree(locationId: string, position: string, positionType: string) {
	const query = gql`
		query getWashoutsSidePanelTree($locationId: String!, $position: String!, $positionType: String) {
			getWashoutsSidePanelTree(locationId: $locationId, position: $position, positionType: $positionType) {
				...SidePanelTree
			}
		}
		${SidePanelTreeFragment}
	`;

	const variables = {
		locationId,
		position,
		positionType,
	};

	try {
		const { data } = await apiInstance.post("/graphql", { query, variables });
		return data.data?.getWashoutsSidePanelTree;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getCommissionCyclesCitCommissionPayout(locationId?: string, commissionCycleId?: string) {
	const query = gql`
		query getCommissionCycle($locationId: String!, $id: String!) {
			getCommissionCycle(locationId: $locationId, id: $id) {
				_id
				citCommissionPayoutInitialPositions
			}
		}
	`;

	const variables = {
		locationId,
		id: commissionCycleId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getCommissionCycle;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getSystemSettingsCitCommissionPayout(locationId?: string) {
	const query = gql`
		query getSystemSettings($locationId: String!) {
			getSystemSettings(locationId: $locationId) {
				_id
				citCommissionPayout {
					toPositions
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSystemSettings;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getSystemSettingsWashoutReviewers(locationId?: string) {
	const query = gql`
		query getSystemSettings($locationId: String!) {
			getSystemSettings(locationId: $locationId) {
				_id
				washoutReviewers {
					reviewerId {
						_id
						fullName
						position
					}
					position
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSystemSettings;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getUserPosition(userId: string, locationId: string) {
	const query = gql`
		query getUser($_id: String!, $locationId: String!) {
			getUser(_id: $_id, locationId: $locationId) {
				_id
				position
			}
		}
	`;

	const variables = {
		locationId,
		_id: userId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getUser;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

interface IGetCommissionSupportTeamSystemSettings extends Pick<ISystemSettings, "commissionSupportTeam" | "washoutReviewers"> {}

export async function getCommissionSupportTeamSystemSettings(locationId: string) {
	const query = gql`
		query getSystemSettings($locationId: String!, $filter: SystemSettingsFilter) {
			getSystemSettings(locationId: $locationId, filter: $filter) {
				commissionSupportTeam {
					_id
					fullName
					position
					employeeId
					presignedProfilePicture
					communications {
						communicationType
						usage
						value
					}
				}
				washoutReviewers {
					reviewerId {
						position
						_id
						employeeId
						fullName
						presignedProfilePicture
						communications {
							communicationType
							usage
							value
						}
					}
					position
				}
			}
		}
	`;

	const variables = {
		locationId,
		filter: {
			resolveProfilePicture: true,
		},
	};

	try {
		const response: GraphqlQueryResponse<"getSystemSettings", IGetCommissionSupportTeamSystemSettings> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.getSystemSettings;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getAllReviewersSystemSettings(locationId: string) {
	const query = gql`
		query getSystemSettings($locationId: String!, $filter: SystemSettingsFilter) {
			getSystemSettings(locationId: $locationId, filter: $filter) {
				washoutReviewers {
					reviewerId {
						position
						_id
						employeeId
						fullName
						presignedProfilePicture
						communications {
							communicationType
							usage
							value
						}
					}
					position
				}
			}
		}
	`;

	const variables = {
		locationId,
		filter: {
			resolveProfilePicture: true,
		},
	};

	try {
		const response: GraphqlQueryResponse<"getSystemSettings", IGetAllReviewersSystemSettings> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSystemSettings;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export const getChangeRequests = async ({
	locationId,
	commissionCycleId,
	filter,
}: {
	locationId: string;
	commissionCycleId?: string;
	filter?: {
		userId?: string;
		origin?: "SUPPORT" | "REVIEW";
		supportAssigned?: boolean;
		payPeriodId?: string;
		position?: string;
	};
}) => {
	const query = gql`
		query getChangeRequests($locationId: String!, $commissionCycleId: String, $filter: ChangeRequestFilterInput) {
			getChangeRequests(locationId: $locationId, commissionCycleId: $commissionCycleId, filter: $filter) {
				...ChangeRequestResponse
			}
		}
		${ChangeRequestResponseFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		filter,
	};
	try {
		const response: GraphqlQueryResponse<"getChangeRequests", IGetChangeRequestsByCommissionCycle> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.getChangeRequests;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
};

export async function generateWashoutsPDF(locationId: string, position: string, isDepartmental: boolean, washoutId: string) {
	const query = gql`
		query generateWashoutsPDF($locationId: String!, $isDepartmental: Boolean, $position: String!, $input: WashoutFilterInput) {
			generateWashoutsPDF(locationId: $locationId, isDepartmental: $isDepartmental, position: $position, input: $input) {
				url
			}
		}
	`;
	const variables = {
		locationId,
		position,
		isDepartmental,
		input: {
			_id: washoutId,
		},
	};
	try {
		const response: GraphqlQueryResponse<"generateWashoutsPDF", IWashoutPdfUrl> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.generateWashoutsPDF;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
